import { MapMarker } from "@/types/map-marker"

const DEFAULT_MIN_PIXEL_DISTANCE = 40

interface WorldPixel {
  x: number
  y: number
}

export interface SelectSpacedMarkersOptions {
  zoom: number
  minPixelDistance?: number
  avoid?: MapMarker[]
}

/** Web Mercator world pixel coordinates at the given zoom level. */
export function latLngToWorldPixels(
  latitude: number,
  longitude: number,
  zoom: number
): WorldPixel {
  const scale = 256 * 2 ** zoom
  const x = ((longitude + 180) / 360) * scale
  const sinLat = Math.sin((latitude * Math.PI) / 180)
  const y =
    (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale
  return { x, y }
}

function pixelDistance(a: WorldPixel, b: WorldPixel): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.hypot(dx, dy)
}

function markerWorldPixel(marker: MapMarker, zoom: number): WorldPixel | null {
  if (!Number.isFinite(marker.latitude) || !Number.isFinite(marker.longitude)) {
    return null
  }
  return latLngToWorldPixels(marker.latitude, marker.longitude, zoom)
}

/**
 * Greedily keeps markers that are at least minPixelDistance apart in screen space.
 * Input order is priority order; avoid markers are always kept and block lower layers.
 */
export function selectSpacedMarkers(
  markers: MapMarker[],
  {
    zoom,
    minPixelDistance = DEFAULT_MIN_PIXEL_DISTANCE,
    avoid = [],
  }: SelectSpacedMarkersOptions
): MapMarker[] {
  const acceptedPixels: WorldPixel[] = []

  for (const marker of avoid) {
    const pixel = markerWorldPixel(marker, zoom)
    if (pixel) acceptedPixels.push(pixel)
  }

  const accepted: MapMarker[] = []
  for (const marker of markers) {
    const pixel = markerWorldPixel(marker, zoom)
    if (!pixel) continue

    const tooClose = acceptedPixels.some(
      (acceptedPixel) => pixelDistance(pixel, acceptedPixel) < minPixelDistance
    )
    if (tooClose) continue

    accepted.push(marker)
    acceptedPixels.push(pixel)
  }

  return accepted
}
